






import { defineComponent } from '@nuxtjs/composition-api';

// Shared across instances so a second mount waits for the in-flight load instead of
// resolving early against a script tag that hasn't executed yet.
let scriptPromise: Promise<void> | null = null;

declare global {
  interface Window {
    OneTrust: {
      NoticeApi: {
        Initialized: Promise<void>;
        LoadNotices: (noticeUrls: string[], flag: boolean) => void;
      };
    };
  }
}

export default defineComponent({
  name: 'PolicyDocument',
  props: {
    id: {
      type: String,
      default: '',
    },
    noticeUrl: {
      type: String,
      default: '',
    },
    /**
     * Base64 JSON of OneTrust's `contentApiUrl` + `metadataApiUrl`, supplied by OneTrust
     * alongside the notice URL. Pass it only for API-backed ("migrated") notices, where
     * no static `privacynotice.json` exists and fetching the notice URL 404s. Leaving it
     * empty keeps the loader on its default path: fetch the notice URL as a static file.
     */
    settings: {
      type: String,
      default: '',
    },
  },
  computed: {
    divId(): string {
      return `otnotice-${this.id}`;
    },
  },
  mounted() {
    this.loadScript(
      'https://privacyportal-cdn.onetrust.com/privacy-notice-scripts/otnotice-1.0.min.js',
      'otprivacy-notice-script',
    )
      .then(() => {
        if (window.OneTrust && window.OneTrust.NoticeApi) {
          window.OneTrust.NoticeApi.Initialized.then(() => {
            window.OneTrust.NoticeApi.LoadNotices([this.noticeUrl], false);
          });
        } else {
          console.error('OneTrust API not available.');
        }
      })
      .catch((error: any) => {
        console.error(error);
      });
  },
  methods: {
    loadScript(src: string, id: string): Promise<void> {
      if (scriptPromise) {
        return scriptPromise;
      }

      scriptPromise = new Promise<void>((resolve, reject) => {
        if (document.getElementById(id)) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.type = 'text/javascript';
        script.id = id;
        if (this.settings) {
          // Must be set before the script executes — the loader reads it off its own tag.
          script.setAttribute('settings', this.settings);
        }
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

        document.head.appendChild(script);
      }).catch((error) => {
        // Let a later mount retry rather than caching the failure forever.
        scriptPromise = null;
        throw error;
      });

      return scriptPromise;
    },
  },
});
